import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "react-toastify";
import { useLoginUserLazyQueryHook } from "@/hooks/queries";
import { useTranslation } from "react-i18next";
import { signInWithEmail, signInWithGoogle, signInWithMicrosoft } from "@/firebase";
import { FcGoogle } from "react-icons/fc";
import { FaMicrosoft } from "react-icons/fa";
import { UserCredential } from "firebase/auth";
import { CustomToastComponent } from "@/components/shared";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { internationalEmail } from "@/utils/form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { CircleLoader } from "@/components/shared/loaders";
import { useTransition } from "react";
import PasswordInput from "@/components/ui/password-input";

/**
 * Zod schema for login form validation
 * Email: International email validation supporting German umlauts (ä, ö, ü, ß)
 * Password: Required string field
 */
const createLoginFormSchema = (t: (_key: string) => string) => {
  return z.object({
    email: internationalEmail(t("login.validation.emailinvalid")),
    password: z.string().min(1, t("login.validation.passwordrequired"))
  });
};

type LoginFormData = z.infer<ReturnType<typeof createLoginFormSchema>>;

const Login = () => {

  const { t } = useTranslation(["translation", "toasts"]);
  const navigate = useNavigate();
  const { loginUser, loading } = useLoginUserLazyQueryHook();
  const [isSubmitting, startSubmitting] = useTransition();

  // Initialize form with Zod validation
  const loginFormSchema = createLoginFormSchema(t);
  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onChange",
  });

  // Function to handle sign in with password
  // Gets the token from the user credential from Firebase and logs in the user with that token
  const handleSignInWithPassword = async (email: string, password: string) => {
    startSubmitting(() =>
      signInWithEmail(email, password).then(async (userCredential) => {
        const token = await userCredential.user.getIdToken(true);
        loginUser({ variables: { oAuthToken: token } });
      }).catch((_error) => {
        const isInvalidCredential =
          typeof _error?.message === "string"
            ? _error.message.includes("invalid-credential")
            : false;
        handleAuthError(isInvalidCredential);
      }));
  };

  // Form submit handler
  const onSubmit = (data: LoginFormData) => {
    const { email, password } = data;
    handleSignInWithPassword(email, password);
  };

  // Form validation error handler
  const onInvalid = (errors: any) => {
    console.error("Form validation errors:", errors);
  };

  // Common error handler
  const handleAuthError = (isInvalidCredential?: boolean) => {
    toast(CustomToastComponent, {
      toastId: "login-auth-error",
      data: {
        title: isInvalidCredential ? t("toasts:toasts.invalidcredentials") : t("toasts:toasts.somethingwentwrong"),
        type: "error",
      }
    });
  };

  /**
   * Handles the OAuth login process by executing the provided authentication function,
   * retrieving an ID token, and logging in the user with that token.
   *
   * @param {() => Promise<UserCredential>} fn - A function that initiates OAuth login and returns a Promise resolving to a UserCredential.
   * @returns {Promise<void>} A Promise that resolves when the login process is complete.
   */
  const handleOAuthLogin = async (fn: () => Promise<UserCredential>): Promise<void> => {
    try {
      const result = await fn();
      const token = await result.user.getIdToken(true);
      loginUser({ variables: { oAuthToken: token } });
    } catch (_error) {
      handleAuthError();
    }
  };

  return (
    <div className="w-full max-w-md p-8">
      <div className="flex flex-col gap-2 text-center mb-6">
        <h1 className="text-2xl font-bold">{t("login.welcomeback")}</h1>
        <p className="text-sm text-slate-600">{t("login.signincontinue")}</p>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, onInvalid)}
          data-cy="login-email-container"
          className="w-full space-y-4 mb-4"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t("login.inputs.email")}
                    autoComplete="email"
                    className="px-4 py-2 rounded-md"
                    {...field}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        form.handleSubmit(onSubmit, onInvalid)();
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-2">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <PasswordInput
                      hideStrengthUI
                      placeholder={t("login.inputs.password")}
                      autoComplete="password"
                      className="px-4 py-2 rounded-md"
                      {...field}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          form.handleSubmit(onSubmit, onInvalid)();
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-center justify-between gap-2 text-sm mt-2">
              <p className="text-gray-800 font-light">
                {t("login.forgotpassword")}
              </p>
              <a onClick={() => { navigate("/reset-password"); }} className="text-blue-400 cursor-pointer">
                {t("login.resetpassword")}
              </a>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full transition-all duration-300"
            disabled={!form.formState.isValid || loading || isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <CircleLoader
                  visible={isSubmitting || loading}
                  height={20}
                  width={20}
                  strokeWidth={2}
                  ariaLabel="loading..."
                />
                <p>
                  {t("login.sigingin")}
                </p>
              </div>
            ) : (
              <p>
                {t("login.signin")}
              </p>
            )
            }
          </Button>
        </form>
      </Form>
      <div className="relative my-4 py-4">
        <Separator className="" />
        <p className="text-muted-foreground absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-sm shrink-0 px-4 bg-white">
          {t("login.orcontinuewith")}
        </p>
      </div>

      <div className="flex md:flex-row flex-col gap-4">
        <Button
          onClick={() => { handleOAuthLogin(signInWithGoogle); }}
          variant="outline"
          className="flex-1 text-gray-800 flex items-center justify-center transition-all duration-300"
        >
          <FcGoogle size={20} className="mr-2" />
          Google
        </Button>

        <Button
          onClick={() => { handleOAuthLogin(signInWithMicrosoft); }}
          className="flex-1 bg-[#2F2F2F] hover:bg-[#1F1F1F] text-white flex items-center justify-center transition-all duration-300"
        >
          <FaMicrosoft size={20} className="mr-2" />
          Microsoft
        </Button>
      </div>
      <div className="flex flex-col items-center justify-center mt-8 text-sm gap-2">
        <p className="text-gray-800 font-medium">
          {t("login.notaccount")}{" "}
          <a
            href="mailto:contact@wisebid.cloud"
            className="text-blue-400 hover:underline focus-visible:underline focus:outline-none transition-colors"
          >
            {t("login.contactsupport")}
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
